import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { sceneVisualManifest } from "@/assets/v2/manifest";
import type { V2Scene, ViewportProfile } from "../types";
import { sceneOrder, sceneVisualKey } from "../types";

function sourceFor(scene: V2Scene, profile: ViewportProfile, ticketArt?: "mobile" | "desktop"): string {
  const asset = sceneVisualManifest[sceneVisualKey[scene]];
  return visualKeyFor(scene, profile, ticketArt) === "mobile" ? asset.mobile : asset.desktop;
}

function positionFor(scene: V2Scene, profile: ViewportProfile, ticketArt?: "mobile" | "desktop"): string {
  const asset = sceneVisualManifest[sceneVisualKey[scene]];
  return visualKeyFor(scene, profile, ticketArt) === "mobile" ? asset.mobilePosition : asset.desktopPosition;
}

/** 票幕可以按窗口朝向改用竖版票版（竖屏下横版素材会连叠加层一起被裁掉）。 */
function visualKeyFor(scene: V2Scene, profile: ViewportProfile, ticketArt?: "mobile" | "desktop"): "mobile" | "desktop" {
  if (scene === "ticket" && ticketArt) return ticketArt;
  return profile === "mobile" ? "mobile" : "desktop";
}

/**
 * 桌面端极轻的镜头视差：整幅背景（含前景雾对比层）随指针缓慢平移，
 * 让"湖面"有可呼吸的纵深。移动/触屏与 reduced-motion 下自动停用。
 */
function useParallax(): RefObject<HTMLDivElement | null> {
  const layerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = layerRef.current;
    if (!element) return;
    if (!window.matchMedia("(min-width: 1100px) and (pointer: fine) and (hover: hover)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    element.style.transition = "transform .55s cubic-bezier(.22, 1, .36, 1)";
    let frame = 0;
    const apply = (x: number, y: number) => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const sx = Math.max(-1, Math.min(1, x)) * 1.5;
        const sy = Math.max(-1, Math.min(1, y)) * 1.05;
        element.style.transform = `translate3d(${sx}%, ${sy}%, 0)`;
      });
    };
    const onMove = (event: PointerEvent) => {
      apply(event.clientX / window.innerWidth * 2 - 1, event.clientY / window.innerHeight * 2 - 1);
    };
    const onLeave = () => apply(0, 0);
    const onBlur = () => { element.style.transform = ""; };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave, { passive: true });
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onBlur);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return layerRef;
}

export function SceneBackground({ scene, previousScene, profile, ticketArt, children }: { scene: V2Scene; previousScene: V2Scene | null; profile: ViewportProfile; ticketArt?: "mobile" | "desktop"; children?: ReactNode }) {
  const parallaxRef = useParallax();

  useEffect(() => {
    const nextScene = sceneOrder[sceneOrder.indexOf(scene) + 1];
    if (!nextScene) return;
    const image = new Image();
    image.decoding = "async";
    image.src = sourceFor(nextScene, profile, ticketArt);
  }, [profile, scene, ticketArt]);

  return (
    /* 有 children 时不能整层 aria-hidden——票面上的文字是真实内容，
       否则屏幕阅读器读不到（背景图本身 alt="" 仍是装饰）。 */
    <div className="v2-background" aria-hidden={children ? undefined : true}>
      <div className="v2-bg-parallax" ref={parallaxRef}>
        {previousScene ? <img className="v2-background-image is-previous" src={sourceFor(previousScene, profile, ticketArt)} alt="" /> : null}
        {/* 票幕的 key 要带上票版朝向（换素材就得重放"靠岸"动画），
            其他幕不必——免得窗口跨过正方形时白重放一次。 */}
        <div className="v2-bg-current" key={`${profile}-${scene}${scene === "ticket" ? `-${ticketArt ?? "auto"}` : ""}`}>
          <img className="v2-background-image is-current" src={sourceFor(scene, profile, ticketArt)} alt="" fetchPriority="high" style={{ objectPosition: positionFor(scene, profile, ticketArt) }} />
          {/* 叠加层必须待在 .v2-bg-current 里：这一层带 50s 漂移（scale 最高 1.06），
              图片自身还有缓推动画——放在外面就永远对不齐（实测差到约 10%）。 */}
          {children}
        </div>
        <div className="v2-background-contrast" />
      </div>
    </div>
  );
}
